import dayjs from "dayjs";
import { CheckCircle2, CircleAlert, ClipboardCheck } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import type { StatusHealth } from "@/api/types/foreignNational";
import type { ForeignNationalAddressHistoryEntry } from "@/api/types/foreignNationalAddressHistory";
import type { ForeignNationalEmploymentHistoryEntry } from "@/api/types/foreignNationalEmploymentHistory";
import type { ForeignNationalPassport } from "@/api/types/foreignNationalRecords";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import { DashboardCard } from "@/features/dashboard/components/cards/DashboardCard";
import { useNoticeBrief } from "@/features/keyFacts";
import { cn } from "@/lib/utils";

import { useAskForGaps } from "../../components/CoverageBar";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useHistoryCoverage } from "../../hooks/useHistoryCoverage";
import { isOpenCaseStatus } from "../../lib/coverage";
import { matterWorkflowFlags } from "../cases/casesModel";
import {
  isNoticeWorkflow,
  readinessChecks,
  responseChecks,
  type ReadinessCheck,
  type ReadinessTarget
} from "./readinessModel";

const addressPeriod = (e: ForeignNationalAddressHistoryEntry) => ({
  start: e.start_date,
  end: e.end_date,
  current: Boolean(e.is_current)
});

const jobPeriod = (e: ForeignNationalEmploymentHistoryEntry) => ({
  start: e.start_date,
  end: e.end_date,
  current: Boolean(e.is_current),
  label: `${e.job_title ?? ""} ${e.employer?.legalBusinessName ?? ""} ${
    e.notes ?? ""
  }`
});

type Records = {
  addresses: ForeignNationalAddressHistoryEntry[];
  jobs: ForeignNationalEmploymentHistoryEntry[];
  passports: ForeignNationalPassport[];
};

const EMPTY: Records = { addresses: [], jobs: [], passports: [] };

/** The tabs a check's action can open (the rest are handled in place). */
export type ReadinessTab = Exclude<ReadinessTarget, `ask_${string}` | "case">;

export type Readiness = {
  /** False without an open case: there is nothing to measure against. */
  hasOpenCase: boolean;
  visa: string;
  loaded: boolean;
  /** The profile checks (every case's forms need them). */
  checks: ReadinessCheck[];
  done: number;
  pct: number;
  /**
   * A notice case's "Response readiness": its issues against the response
   * draft. `null` for filing workflows.
   */
  response: { label: string; checks: ReadinessCheck[]; loaded: boolean } | null;
  /** Runs a check's action: asks the client for history gaps, or opens a tab. */
  act: (
    target: ReadinessTarget,
    onOpen: (target: ReadinessTab) => void
  ) => void;
};

/**
 * How ready the profile is for the newest open case: status, passport validity
 * through filing, history coverage, birth details and citizenship.
 */
export const useReadiness = (
  health: StatusHealth | null | undefined
): Readiness => {
  const { clientId, client, matters } = useClientProfile();
  const [records, setRecords] = useState<Records | null>(null);
  const askAddress = useAskForGaps("address");
  const askEmployment = useAskForGaps("employment");

  const openCase = useMemo(
    () => matters.matters.find((m) => isOpenCaseStatus(m.case?.status)),
    [matters.matters]
  );

  useEffect(() => {
    if (!openCase) return undefined;
    let live = true;
    Promise.all([
      ForeignNationalsAPI.listAddressHistoryEntries(clientId).catch(() => []),
      ForeignNationalsAPI.listEmploymentHistoryEntries(clientId).catch(
        () => []
      ),
      ForeignNationalsAPI.listPassports(clientId).catch(() => [])
    ]).then(([addresses, jobs, passports]) => {
      if (live) setRecords({ addresses, jobs, passports });
    });
    return () => {
      live = false;
    };
  }, [clientId, openCase]);

  const loaded = records !== null;
  const { addresses, jobs, passports } = records ?? EMPTY;
  const address = useHistoryCoverage(
    "address",
    addresses,
    addressPeriod,
    loaded
  );
  const employment = useHistoryCoverage("employment", jobs, jobPeriod, loaded);

  const notice = openCase
    ? isNoticeWorkflow(matterWorkflowFlags(openCase))
    : false;
  const brief = useNoticeBrief(openCase?.individual_id ?? "", notice);
  const navigate = useNavigate();
  const response = notice
    ? {
        label: brief.data?.brief?.label ?? "Notice",
        checks: brief.loading ? [] : responseChecks(brief.data),
        loaded: !brief.loading
      }
    : null;

  const visa = String(openCase?.case?.visa ?? "").trim() || "Open case";
  const deadline = openCase?.case?.filing_deadline
    ? dayjs(String(openCase.case.filing_deadline))
    : null;
  const firstName = (client?.first_name ?? "").trim() || null;
  const checks =
    openCase && loaded
      ? readinessChecks({
          client,
          health,
          passports,
          address: address.coverage,
          employment: employment.coverage,
          filingBy: deadline?.isValid() ? deadline : dayjs(),
          firstName
        })
      : [];
  const done = checks.filter((c) => c.ok).length;
  const pct = checks.length ? Math.round((done / checks.length) * 100) : 0;

  const act: Readiness["act"] = (target, onOpen) => {
    if (target === "case") {
      if (openCase)
        navigate(
          caseWorkspacePath({
            individualId: openCase.individual_id,
            foreignNationalId: openCase.individual?.foreign_national_id ?? null
          })
        );
    } else if (target === "ask_address")
      void askAddress.ask(address.coverage?.gaps ?? []);
    else if (target === "ask_employment")
      void askEmployment.ask(employment.coverage?.gaps ?? []);
    else onOpen(target);
  };

  return {
    hasOpenCase: Boolean(openCase),
    visa,
    loaded,
    checks,
    done,
    pct,
    response,
    act
  };
};

/** A thin bar for the share of checks that pass. */
export const ReadinessMeter: FC<{ pct: number; className?: string }> = ({
  pct,
  className
}) => (
  <span
    className={cn(
      "h-1 w-full overflow-hidden rounded-full bg-fill-2",
      className
    )}
    role="progressbar"
    aria-valuenow={pct}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-label="Profile readiness"
  >
    <span
      className={cn(
        "block h-full rounded-full",
        pct === 100 ? "bg-success" : "bg-warn"
      )}
      style={{ width: `${pct}%` }}
    />
  </span>
);

type Props = {
  health: StatusHealth | null | undefined;
  onOpen: (target: ReadinessTab) => void;
};

/**
 * The Overview's "Ready to File" card, or "Response Readiness" for a notice
 * case. Renders nothing without an open case.
 */
export const ReadinessCard: FC<Props> = ({ health, onOpen }) => {
  const readiness = useReadiness(health);
  const { hasOpenCase, visa, act, response } = readiness;

  if (!hasOpenCase) return null;
  const checks = response ? response.checks : readiness.checks;
  const loaded = response ? response.loaded : readiness.loaded;
  const done = checks.filter((c) => c.ok).length;
  const pct = checks.length ? Math.round((done / checks.length) * 100) : 0;

  return (
    <DashboardCard
      icon={ClipboardCheck}
      title={response ? "Response Readiness" : "Ready to File"}
      count={null}
      isLoading={!loaded}
      isEmpty={false}
      emptyTitle=""
      testId="readiness-card"
      controls={<Tag>{response ? `${response.label} · ${visa}` : visa}</Tag>}
    >
      <li className="flex items-center gap-3 px-3 pt-2.5 pb-1">
        <span
          className="text-xl font-medium tabular-nums"
          data-testid="readiness-percent"
        >
          {pct}%
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <ReadinessMeter pct={pct} />
          <span className="text-sm text-fg-3">
            {response
              ? `${done} of ${checks.length} issues ready`
              : `${done} of ${checks.length} profile checks pass`}
          </span>
        </span>
      </li>
      {checks.map((c) => (
        <li
          key={c.id}
          className="flex items-center gap-2 px-3 py-1"
          data-testid={c.ok ? "readiness-ok" : "readiness-open"}
        >
          {c.ok ? (
            <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          ) : (
            <CircleAlert className="size-3.5 shrink-0 text-warn" />
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-base",
              c.ok && "text-fg-3"
            )}
          >
            {c.text}
          </span>
          {c.action ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => c.action && act(c.action.target, onOpen)}
            >
              {c.action.label}
            </Button>
          ) : null}
        </li>
      ))}
    </DashboardCard>
  );
};
